/**
 * OpenAI Service for "Find the Image" Mini-Game
 * Routes all OpenAI API calls through a Netlify serverless function
 * to avoid CORS issues and keep the API key secure on the server.
 */

const PROXY_URL = '/.netlify/functions/openai';

/**
 * Generate a scene image with a hidden target object using DALL-E 3
 * @param {string} scenePrompt - Description of the scene (e.g., "busy farmer's market")
 * @param {string} targetObject - The object to hide in the scene (e.g., "pink pony")
 * @returns {Promise<{url: string, revisedPrompt: string}>} Generated image URL and revised prompt
 */
export const generateScene = async (scenePrompt, targetObject) => {
  if (!scenePrompt || !targetObject) {
    throw new Error('Both scene prompt and target object are required.');
  }

  const fullPrompt = `Create a detailed, busy ${scenePrompt} illustration in a colorful, vibrant style. ` +
    `Hidden somewhere in the scene, include a small but visible ${targetObject}. ` +
    `The ${targetObject} should blend in somewhat but still be findable upon careful inspection. ` +
    `The image should be rich in detail with many elements, objects, and visual distractions to search through. ` +
    `Make the scene feel alive and packed with interesting things to look at. ` +
    `The ${targetObject} should not be in the exact center -- place it in a less obvious location. ` +
    `IMPORTANT: Do NOT include any text, words, letters, numbers, labels, signs, captions, watermarks, or writing of any kind anywhere in the image. The image must be purely visual with zero text.`;

  console.log('[OPENAI] Generating scene via proxy:', {
    scenePrompt,
    targetObject,
    fullPromptLength: fullPrompt.length
  });

  try {
    const response = await fetch(PROXY_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'generate-scene',
        prompt: fullPrompt
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        `Scene generation error: ${response.status} - ${errorData.error || response.statusText}`
      );
    }

    const data = await response.json();
    const imageData = data.data[0];

    console.log('[OPENAI] Scene generated successfully:', {
      revisedPrompt: imageData.revised_prompt?.substring(0, 100) + '...',
      hasUrl: !!imageData.url
    });

    return {
      url: imageData.url,
      revisedPrompt: imageData.revised_prompt
    };
  } catch (error) {
    console.error('[OPENAI] Error generating scene:', error);
    throw error;
  }
};

/**
 * Detect the target object's location in the generated image using GPT-4o Vision
 * @param {string} imageUrl - URL of the generated image
 * @param {string} targetObject - The object to find (e.g., "pink pony")
 * @returns {Promise<{x: number, y: number, width: number, height: number}>} Normalized bounding box (0-1 range)
 */
export const detectTarget = async (imageUrl, targetObject) => {
  if (!imageUrl || !targetObject) {
    throw new Error('Both image URL and target object are required.');
  }

  console.log('[OPENAI] Detecting target via proxy:', {
    targetObject,
    imageUrl: imageUrl.substring(0, 80) + '...'
  });

  const makeDetectionCall = async (attempt) => {
    const systemPrompt = attempt === 1
      ? `You are a precise image analysis tool. This image was specifically generated to contain a hidden "${targetObject}". The object IS in this image — your job is to find it.

CRITICAL RULES:
1. You MUST respond with ONLY valid JSON — no explanation, no caveats, no other text.
2. The object is guaranteed to be in the image. Look carefully at every region.
3. If you are unsure of the exact location, give your best estimate.

Respond with ONLY this JSON format:
{"x": <number>, "y": <number>, "width": <number>, "height": <number>}

Where all values are normalized between 0 and 1:
- x = left edge of the bounding box
- y = top edge of the bounding box  
- width = width of the bounding box
- height = height of the bounding box
- (0,0) is top-left corner, (1,1) is bottom-right corner`
      : `You are an image analysis tool. This is an AI-generated illustration that was created to contain a "${targetObject}" hidden somewhere in the scene.

The object IS present. It may be small, partially obscured, stylized, or blended into the background. Scan the entire image carefully — check all corners, edges, foreground, and background.

You MUST respond with ONLY valid JSON, nothing else:
{"x": <number>, "y": <number>, "width": <number>, "height": <number>}

All values are normalized 0-1. (0,0) = top-left. Give your best estimate even if uncertain.`;

    const userPrompt = attempt === 1
      ? `Locate the "${targetObject}" in this image. Return its bounding box as normalized coordinates (0-1).`
      : `Look very carefully at this entire image. A "${targetObject}" was placed somewhere in this AI-generated scene. It might be small or stylized. Find it and return the bounding box coordinates. You MUST return JSON only.`;

    const response = await fetch(PROXY_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'detect-target',
        messages: [
          {
            role: 'system',
            content: systemPrompt
          },
          {
            role: 'user',
            content: [
              { type: 'text', text: userPrompt },
              {
                type: 'image_url',
                image_url: {
                  url: imageUrl,
                  detail: 'high'
                }
              }
            ]
          }
        ]
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        `Target detection error: ${response.status} - ${errorData.error || response.statusText}`
      );
    }

    const data = await response.json();
    return data.choices[0]?.message?.content?.trim();
  };

  const parseResponse = (content) => {
    // Handle potential markdown code block wrapping
    const jsonStr = content.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
    const bbox = JSON.parse(jsonStr);

    const { x, y, width, height } = bbox;
    if (
      typeof x !== 'number' || typeof y !== 'number' ||
      typeof width !== 'number' || typeof height !== 'number' ||
      x < 0 || x > 1 || y < 0 || y > 1 ||
      width <= 0 || width > 1 || height <= 0 || height > 1
    ) {
      throw new Error(`Invalid bounding box values: ${JSON.stringify(bbox)}`);
    }

    return bbox;
  };

  try {
    // Attempt 1
    console.log('[OPENAI] Detection attempt 1...');
    let content = await makeDetectionCall(1);
    console.log('[OPENAI] GPT-4o Vision response (attempt 1):', content);

    try {
      const bbox = parseResponse(content);
      console.log('[OPENAI] Target detected successfully:', bbox);
      return bbox;
    } catch (parseError1) {
      console.warn('[OPENAI] Attempt 1 failed to parse, retrying with stronger prompt...');
    }

    // Attempt 2 — stronger prompt
    console.log('[OPENAI] Detection attempt 2...');
    content = await makeDetectionCall(2);
    console.log('[OPENAI] GPT-4o Vision response (attempt 2):', content);

    try {
      const bbox = parseResponse(content);
      console.log('[OPENAI] Target detected on retry:', bbox);
      return bbox;
    } catch (parseError2) {
      console.error('[OPENAI] Both detection attempts failed.');
      throw new Error(
        `Could not locate "${targetObject}" in the generated scene. Try a more distinct or colorful object (e.g., "red bicycle", "purple umbrella", "golden trophy").`
      );
    }
  } catch (error) {
    console.error('[OPENAI] Error detecting target:', error);
    throw error;
  }
};

/**
 * Download an image from a URL via the server-side proxy and return it as a Blob.
 * Routes through the proxy to avoid CORS issues with DALL-E CDN URLs.
 * @param {string} url - The image URL to download
 * @returns {Promise<Blob>} The image as a Blob
 */
export const downloadImage = async (url) => {
  console.log('[OPENAI] Downloading image via proxy...');
  const response = await fetch(PROXY_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action: 'download-image',
      url
    })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(`Image download error: ${response.status} - ${errorData.error || response.statusText}`);
  }

  const { base64, contentType } = await response.json();
  
  // Convert base64 back to Blob
  const binaryStr = atob(base64);
  const bytes = new Uint8Array(binaryStr.length);
  for (let i = 0; i < binaryStr.length; i++) {
    bytes[i] = binaryStr.charCodeAt(i);
  }
  
  console.log('[OPENAI] Image downloaded, size:', Math.round(bytes.length / 1024), 'KB');
  return new Blob([bytes], { type: contentType });
};

/**
 * Check if the proxy is reachable (replaces old API key check)
 * @returns {boolean} Always true since the key lives server-side now
 */
export const isConfigured = () => {
  return true;
};
